/**
 * #1408 — the frame an app runs in when it is served on its OWN preview origin
 * (`<label>.<APP_PREVIEW_DOMAIN>`, returned by the session mint as `appPreviewOrigin`).
 *
 * Everywhere else an app frame is opaque-origin (no `allow-same-origin`): that is what keeps
 * served HTML away from Papan's cookies and storage (#185, #312), and it is why the browser
 * never gives such a frame the camera. On the app's own preview origin the page is a real
 * origin on ANOTHER site (pd.app), which no API trusts and nginx serves no API on, so
 * `allow-same-origin` is safe there — and only there. HtmlViewer applies this sandbox only
 * when the frame's URL really is on the origin the server returned (`frameAppOriginFor`).
 */

/** The opaque sandbox every other app frame keeps. */
export const OPAQUE_FRAME_SANDBOX = 'allow-scripts allow-popups allow-forms'

/** On the app's own origin: a real origin, plus downloads. Popups keep the sandbox (no
 *  `allow-popups-to-escape-sandbox`, as on `/s/`), and never top navigation, so the app can't take
 *  over Papan's tab. */
export const APP_ORIGIN_FRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-downloads'

/** What the app may ask the person for there, as on its own link. */
export const APP_ORIGIN_FRAME_ALLOW = 'camera; microphone; display-capture; fullscreen; clipboard-read; clipboard-write'

/**
 * The app origin this frame runs on, or null for an opaque frame. Only when `baseUrl` (what the
 * frame loads) is on exactly `appOrigin`, an https origin — a mismatch keeps the opaque sandbox.
 */
export function frameAppOriginFor(appOrigin: string | null | undefined, baseUrl: string | null | undefined): string | null {
  if (!appOrigin || !baseUrl) return null
  try {
    const a = new URL(appOrigin)
    if (a.protocol !== 'https:' || a.origin !== appOrigin) return null
    return new URL(baseUrl).origin === a.origin ? a.origin : null
  } catch {
    return null
  }
}

/**
 * #1408 R6 — where to post to an app frame, from the frame ELEMENT: its exact origin when it runs on
 * the app's own preview origin (HtmlViewer gave it `allow-same-origin` only then), else `'*'` (an
 * opaque frame's origin is 'null', which no targetOrigin can name). A frame the app navigated
 * elsewhere then receives nothing. Pair it with {@link frameMessageFrom} on the reply.
 */
export function frameMessageTarget(frame: HTMLIFrameElement | null | undefined): string {
  if (!frame || !(frame.getAttribute('sandbox') ?? '').split(/\s+/).includes('allow-same-origin')) return '*'
  try {
    const u = new URL(frame.getAttribute('src') || frame.src)
    return u.protocol === 'https:' ? u.origin : '*'
  } catch {
    return '*'
  }
}

/** Is a reply from the frame we posted to (`target` from {@link frameMessageTarget})? Source always;
 *  the exact origin too when the frame runs on a real origin. */
export function frameMessageFrom(e: MessageEvent, frameWindow: Window | null | undefined, target: string): boolean {
  return !!frameWindow && e.source === frameWindow && (target === '*' || e.origin === target)
}

/** Where to reply to a request that arrived in `e`: its own origin when real, else `'*'` (opaque). */
export function replyTargetOf(e: MessageEvent): string {
  return e.origin && e.origin !== 'null' ? e.origin : '*'
}
